/**
 * useEmployerLimits.js — RC 1.62 Stabilisation Slice (Part D).
 *
 * Single fetch of `/api/employer/v1/limits` — the canonical
 * server-side projection of operational ceilings (bulk batch, shim
 * timeouts, idempotency window, AND the upload-size limits added
 * in this slice as the resolution to the RC 1.62 Hardcoded Audit's
 * R-8 finding).
 *
 * Doctrine
 * ────────
 *   • One read per mount; no polling. Limits change rarely; the
 *     operator can always refresh the page to pick up a flip.
 *   • Fail-LOUD: returns `{ limits: null, error, loading }` so the
 *     consumer can display "Unavailable" rather than fabricating
 *     a fallback constant. Per the RC 1.62 honesty doctrine, a
 *     missing endpoint MUST NOT silently inflate to a stale FE
 *     constant.
 *   • No localStorage cache — the limits are cheap to fetch and
 *     stale-cache risk would re-introduce drift.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './api';

const DORMANT = Object.freeze({
  limits: null,
  loading: true,
  error: null,
});

export function useEmployerLimits() {
  const [state, setState] = useState(DORMANT);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const limits = await apiFetch('/api/employer/v1/limits');
      setState({ limits, loading: false, error: null });
    } catch (err) {
      setState({
        limits: null,
        loading: false,
        error: err?.message || 'Limits unavailable',
      });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { ...state, refresh };
}

export default useEmployerLimits;
